import type { UseFormReturn, FieldPath, Path, PathValue } from 'react-hook-form'
import { Input, Select, Checkbox, CardHeader } from '@orientamos/ui'
import type { IntakeFormData } from '@orientamos/shared'

interface PartyFormProps<T extends FieldPath<IntakeFormData>> {
  form: UseFormReturn<IntakeFormData>
  basePath: T
  title: string
  description: string
  showBanking?: boolean
  personTypeToggle?: boolean
}

export function PartyForm<T extends FieldPath<IntakeFormData>>({
  form,
  basePath,
  title,
  description,
  showBanking = false,
  personTypeToggle = true,
}: PartyFormProps<T>) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form

  // Helper to create a path
  const path = <K extends string>(field: K): `${T}.${K}` => `${basePath}.${field}` as `${T}.${K}`

  // Helper to get nested error
  const getError = (field: string): string | undefined => {
    const pathParts = `${basePath}.${field}`.split('.')
    let current: unknown = errors
    for (const part of pathParts) {
      if (current && typeof current === 'object' && part in current) {
        current = (current as Record<string, unknown>)[part]
      } else {
        return undefined
      }
    }
    return (current as { message?: string })?.message
  }

  const personType = watch(path('personType') as Path<IntakeFormData>)
  const isLegalEntity = personType === 'legal'

  const bankTypes = [
    { value: 'ahorros', label: 'Cuenta de Ahorros' },
    { value: 'corriente', label: 'Cuenta Corriente' },
  ]

  const banks = [
    { value: 'Bancolombia', label: 'Bancolombia' },
    { value: 'Davivienda', label: 'Davivienda' },
    { value: 'BBVA', label: 'BBVA' },
    { value: 'Banco de Bogota', label: 'Banco de Bogota' },
    { value: 'Banco de Occidente', label: 'Banco de Occidente' },
    { value: 'Banco Popular', label: 'Banco Popular' },
    { value: 'Scotiabank Colpatria', label: 'Scotiabank Colpatria' },
    { value: 'Itau', label: 'Itau' },
    { value: 'Nequi', label: 'Nequi' },
    { value: 'Daviplata', label: 'Daviplata' },
  ]

  return (
    <div className="space-y-6">
      <CardHeader title={title} description={description} />

      {personTypeToggle && (
        <div className="flex gap-4 p-1 bg-neutral-100 rounded-lg w-fit">
          <button
            type="button"
            onClick={() =>
              setValue(
                path('personType') as Path<IntakeFormData>,
                'natural' as PathValue<IntakeFormData, Path<IntakeFormData>>
              )
            }
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              !isLegalEntity
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Persona Natural
          </button>
          <button
            type="button"
            onClick={() =>
              setValue(
                path('personType') as Path<IntakeFormData>,
                'legal' as PathValue<IntakeFormData, Path<IntakeFormData>>
              )
            }
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              isLegalEntity
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Persona Juridica
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLegalEntity && (
          <>
            <Input
              label="Razon Social"
              placeholder="Nombre de la empresa"
              {...register(path('companyName') as Path<IntakeFormData>)}
              error={getError('companyName')}
            />
            <Input
              label="NIT"
              placeholder="Ej: 900123456-1"
              {...register(path('nit') as Path<IntakeFormData>)}
              error={getError('nit')}
            />
            <Input
              label="Nombre del Representante Legal"
              placeholder="Nombre completo"
              {...register(path('legalRepName') as Path<IntakeFormData>)}
              error={getError('legalRepName')}
            />
            <Input
              label="Cedula del Representante Legal"
              placeholder="Numero de cedula"
              {...register(path('legalRepId') as Path<IntakeFormData>)}
              error={getError('legalRepId')}
            />
          </>
        )}

        <Input
          label={isLegalEntity ? 'Nombre de Contacto' : 'Nombre'}
          placeholder="Nombre"
          {...register(path('firstName') as Path<IntakeFormData>)}
          error={getError('firstName')}
        />
        <Input
          label={isLegalEntity ? 'Apellido de Contacto' : 'Apellido'}
          placeholder="Apellido"
          {...register(path('lastName') as Path<IntakeFormData>)}
          error={getError('lastName')}
        />
        <Input
          label="Numero de Identificacion"
          placeholder="Cedula o documento"
          {...register(path('idNumber') as Path<IntakeFormData>)}
          error={getError('idNumber')}
        />
        <Input
          label="Lugar de Expedicion"
          placeholder="Ciudad de expedicion"
          {...register(path('idIssuedAt') as Path<IntakeFormData>)}
          error={getError('idIssuedAt')}
        />

        {!isLegalEntity && (
          <Input
            label="Profesion u Ocupacion"
            placeholder="Ej: Ingeniero"
            {...register(path('profession') as Path<IntakeFormData>)}
            error={getError('profession')}
          />
        )}

        <Input
          label="Telefono"
          type="tel"
          placeholder="Ej: 3001234567"
          {...register(path('phone') as Path<IntakeFormData>)}
          error={getError('phone')}
        />
        <Input
          label="Correo Electronico"
          type="email"
          placeholder="correo@ejemplo.com"
          {...register(path('email') as Path<IntakeFormData>)}
          error={getError('email')}
        />
        <Input
          label="Correo para Facturas (opcional)"
          type="email"
          placeholder="facturacion@ejemplo.com"
          {...register(path('invoiceEmail') as Path<IntakeFormData>)}
          error={getError('invoiceEmail')}
        />

        <div className="md:col-span-2">
          <Input
            label="Direccion de Residencia"
            placeholder="Direccion completa"
            {...register(path('address') as Path<IntakeFormData>)}
            error={getError('address')}
          />
        </div>

        <Input
          label="Ciudad"
          placeholder="Ej: Medellin"
          {...register(path('city') as Path<IntakeFormData>)}
          error={getError('city')}
        />
      </div>

      {showBanking && (
        <div className="pt-6 mt-6 border-t border-neutral-200">
          <h4 className="text-base font-medium text-neutral-900 mb-4">Informacion Bancaria</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Banco"
              options={banks}
              {...register(path('banking.bankName') as Path<IntakeFormData>)}
              error={getError('banking.bankName')}
            />
            <Select
              label="Tipo de Cuenta"
              options={bankTypes}
              {...register(path('banking.accountType') as Path<IntakeFormData>)}
              error={getError('banking.accountType')}
            />
            <Input
              label="Numero de Cuenta"
              placeholder="Numero de cuenta"
              {...register(path('banking.accountNumber') as Path<IntakeFormData>)}
              error={getError('banking.accountNumber')}
            />
            <Input
              label="Nombre del Titular"
              placeholder="Como aparece en el banco"
              {...register(path('banking.accountHolderName') as Path<IntakeFormData>)}
              error={getError('banking.accountHolderName')}
            />
            <Input
              label="Cedula del Titular"
              placeholder="Numero de cedula"
              {...register(path('banking.accountHolderId') as Path<IntakeFormData>)}
              error={getError('banking.accountHolderId')}
            />
          </div>
        </div>
      )}

      <div className="pt-6 mt-6 border-t border-neutral-200 space-y-4">
        <h4 className="text-base font-medium text-neutral-900">Autorizaciones</h4>

        <Checkbox
          {...register(path('dataAuthorizationAccepted') as Path<IntakeFormData>)}
          label="Autorizacion de Tratamiento de Datos"
          description="Autorizo el tratamiento de mis datos personales de acuerdo con la politica de privacidad"
          error={getError('dataAuthorizationAccepted')}
        />

        <Checkbox
          {...register(path('referenceCheckAuthorized') as Path<IntakeFormData>)}
          label="Autorizacion de Consulta de Referencias"
          description="Autorizo la consulta de referencias comerciales y financieras"
        />
      </div>
    </div>
  )
}
